/** A discrete, two-handle range backed by Shopify's original list-filter values. */
class BlankabricCharacteristicFilter extends HTMLElement {
  #controller;
  #observer;
  #drag = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('input', this.#onInput, options);
    this.addEventListener('change', this.#onChange, options);
    this.addEventListener('click', this.#onClick, options);
    this.addEventListener('pointerdown', this.#onPointerDown, options);
    this.addEventListener('pointermove', this.#onPointerMove, options);
    this.addEventListener('pointerup', this.#onPointerUp, options);
    this.addEventListener('pointercancel', this.#onPointerCancel, options);
    this.addEventListener('lostpointercapture', this.#onPointerCancel, options);
    document.addEventListener('filter:update', this.#onFilterUpdate, options);
    window.addEventListener('popstate', this.#onFilterUpdate, options);

    // Section rendering can morph the existing element without reconnecting it.
    // Watch only its inputs, so updating the labels cannot trigger an observer loop.
    this.#observer?.disconnect();
    this.#observer = new MutationObserver((mutations) => {
      const inputsChanged = mutations.some((mutation) =>
        mutation.type === 'attributes'
          ? mutation.target instanceof HTMLInputElement
          : [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) => node instanceof Element && (node.matches('input') || node.querySelector('input'))
            )
      );
      if (inputsChanged && !this.#drag) this.sync();
    });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'value', 'data-level', 'data-label'],
    });
    this.sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#drag = null;
  }

  get #options() {
    return [...this.querySelectorAll('[data-characteristic-option]')].sort(
      (a, b) => Number(a.dataset.level) - Number(b.dataset.level)
    );
  }

  get #min() { return this.querySelector('[data-min]'); }
  get #max() { return this.querySelector('[data-max]'); }

  /** Sync all form inputs and handles from the URL, including clears and back navigation. */
  sync(url = new URL(window.location.href)) {
    const values = this.#options;
    if (!values.length || !this.#min || !this.#max) return;
    const selected = new Set(url.searchParams.getAll(this.dataset.paramName));
    const indexes = [];
    values.forEach((input, index) => {
      input.checked = selected.has(input.value);
      if (input.checked) indexes.push(index);
    });
    this.#min.max = this.#max.max = String(values.length - 1);
    this.#min.disabled = this.#max.disabled = values.length === 1;
    this.#min.value = String(indexes[0] ?? 0);
    this.#max.value = String(indexes.at(-1) ?? values.length - 1);
    this.#render();

    // Existing links can contain a non-contiguous selection; preserve it until the next drag.
    const contiguous = !indexes.length || indexes.length === indexes.at(-1) - indexes[0] + 1;
    const selection = this.querySelector('[data-selection]');
    if (!contiguous && selection) selection.textContent = indexes.map((index) => values[index].dataset.label).join(', ');
    const status = this.closest('details')?.querySelector('[ref="facetStatus"]');
    if (status) {
      status.classList.remove('bubble', 'facets__bubble');
      status.textContent = indexes.length ? selection?.textContent ?? '' : '';
    }
    const clear = this.querySelector('[data-clear]');
    if (clear) clear.hidden = selected.size === 0;
  }

  #render() {
    const values = this.#options;
    const min = Number(this.#min.value);
    const max = Number(this.#max.value);
    const span = values.length - 1 || 1;
    this.style.setProperty('--bb-characteristic-min', `${(min / span) * 100}%`);
    this.style.setProperty('--bb-characteristic-max', `${(max / span) * 100}%`);
    this.toggleAttribute('data-collapsed', min === max);
    this.#min.setAttribute('aria-valuetext', values[min].dataset.label);
    this.#max.setAttribute('aria-valuetext', values[max].dataset.label);
    const selection = this.querySelector('[data-selection]');
    if (selection) {
      selection.textContent = min === max
        ? values[min].dataset.label
        : `${values[min].dataset.label} ${this.dataset.separator} ${values[max].dataset.label}`;
    }
  }

  #commit(clear = false) {
    const values = this.#options;
    const min = Number(this.#min.value);
    const max = Number(this.#max.value);
    const fullRange = min === 0 && max === values.length - 1;
    const url = new URL(window.location.href);
    url.searchParams.delete(this.dataset.paramName);
    url.searchParams.delete('page');
    if (!clear && !fullRange) {
      values.slice(min, max + 1).forEach((input) => url.searchParams.append(this.dataset.paramName, input.value));
    }

    // Keep the desktop form and the mobile drawer in agreement before another facet is used.
    document.querySelectorAll('blankabric-characteristic-filter').forEach((filter) => {
      if (filter.dataset.paramName === this.dataset.paramName) filter.sync(url);
    });
    if (url.href === window.location.href) return;
    const form = this.closest('facets-form-component');
    if (typeof form?.updateFiltersByURL === 'function') {
      form.updateFiltersByURL(url.toString());
    } else {
      window.location.assign(url.toString());
    }
  }

  #clampInput(input) {
    if (input === this.#min) this.#min.value = String(Math.min(Number(input.value), Number(this.#max.value)));
    if (input === this.#max) this.#max.value = String(Math.max(Number(input.value), Number(this.#min.value)));
    this.#render();
  }

  #onInput = (event) => {
    if (!event.target.matches('[data-min], [data-max]')) return;
    event.stopPropagation();
    this.#clampInput(event.target);
  };

  #onChange = (event) => {
    if (!event.target.matches('[data-min], [data-max]')) return;
    event.stopPropagation();
    this.#clampInput(event.target);
    this.#commit();
  };

  #onClick = (event) => {
    if (!event.target.closest('[data-clear]')) return;
    event.preventDefault();
    this.#commit(true);
  };

  #onFilterUpdate = () => { this.sync(); };

  #indexAt(event) {
    const rect = this.querySelector('[data-track]').getBoundingClientRect();
    const ratio = rect.width ? (event.clientX - rect.left) / rect.width : 0;
    return Math.round(Math.max(0, Math.min(1, ratio)) * (this.#options.length - 1));
  }

  #moveHandle(event) {
    const index = this.#indexAt(event);
    const input = this.#drag.handle === 'min' ? this.#min : this.#max;
    input.value = String(index);
    this.#clampInput(input);
  }

  #onPointerDown = (event) => {
    const track = event.target.closest('[data-track]');
    if (!track || event.button !== 0 || event.isPrimary === false || this.#options.length < 2 || this.#drag) return;
    event.preventDefault();
    const index = this.#indexAt(event);
    const min = Number(this.#min.value);
    const max = Number(this.#max.value);
    const handle = event.target.closest('[data-handle]')?.dataset.handle
      ?? (min === max ? (index < min ? 'min' : 'max') : (Math.abs(index - min) <= Math.abs(index - max) ? 'min' : 'max'));
    this.#drag = { id: event.pointerId, handle, track };
    this.setAttribute('data-dragging', '');
    (handle === 'min' ? this.#min : this.#max).focus({ preventScroll: true });
    track.setPointerCapture(event.pointerId);
    this.#moveHandle(event);
  };

  #onPointerMove = (event) => {
    if (event.pointerId !== this.#drag?.id) return;
    this.#moveHandle(event);
  };

  #endDrag() {
    const drag = this.#drag;
    this.#drag = null;
    this.removeAttribute('data-dragging');
    if (drag?.track.hasPointerCapture(drag.id)) drag.track.releasePointerCapture(drag.id);
  }

  #onPointerUp = (event) => {
    if (event.pointerId !== this.#drag?.id) return;
    this.#moveHandle(event);
    this.#endDrag();
    this.#commit();
  };

  #onPointerCancel = (event) => {
    if (event.pointerId !== this.#drag?.id) return;
    this.#endDrag();
    this.sync();
  };
}

if (!customElements.get('blankabric-characteristic-filter')) {
  customElements.define('blankabric-characteristic-filter', BlankabricCharacteristicFilter);
}
